import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Sparkles, FileImage, Trash2, X, Download } from "lucide-react";
import { motion } from "framer-motion";
import { api, exportUrl, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import BeforeAfter from "@/components/BeforeAfter";
import QualityReport from "@/components/QualityReport";

const ACCEPTED = ".jpg,.jpeg,.png,.tif,.tiff,.fits,.fit,.fts,.xisf";

export default function Dashboard() {
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("naturel");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/modes").then((r) => setModes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return; }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (["jpg", "jpeg", "png", "tif", "tiff", "webp"].includes(ext)) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [file]);

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 500 Mo)"); return; }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) onPick(f);
  };

  const onSubmit = async () => {
    if (!file) { toast.error("Choisissez d'abord une image"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", mode);
    try {
      const { data } = await api.post("/images/process", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Traitement terminé — Score ${data.report.global_score}/100`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du traitement");
    } finally {
      setBusy(false);
    }
  };

  const selectedMode = useMemo(() => modes.find((m) => m.key === mode), [modes, mode]);

  if (result) {
    return (
      <ResultView
        result={result}
        onReset={() => { setResult(null); setFile(null); setPreviewUrl(null); }}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">AstroLift Studio</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-light tracking-tighter">Sublimez automatiquement vos astrophotos.</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl">À partir du signal réellement capturé. Sans génération artificielle. Sans falsification.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="md:col-span-8 rounded-md border border-white/10 bg-card overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="label-eyebrow">Étape 1 · Charger l'image</div>
            <div className="text-[11px] text-muted-foreground">JPG · PNG · TIFF · FITS · max 500 Mo</div>
          </div>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="relative aspect-[16/10] grid place-items-center cursor-pointer transition-colors hover:bg-white/[0.02]"
            onClick={() => inputRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="file-input"
            />
            {previewUrl ? (
              <img src={previewUrl} alt="Aperçu" className="absolute inset-0 w-full h-full object-contain" />
            ) : file ? (
              <div className="text-center">
                <FileImage className="w-10 h-10 mx-auto text-violet-300 mb-3" strokeWidth={1.2} />
                <div className="font-mono text-sm">{file.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{(file.size / 1024 / 1024).toFixed(2)} Mo</div>
                <div className="text-[11px] text-muted-foreground mt-3">Aperçu non disponible pour ce format — traitement supporté.</div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full border border-white/10 grid place-items-center mb-4">
                  <UploadCloud className="w-6 h-6 text-violet-300" strokeWidth={1.2} />
                </div>
                <div className="font-light text-lg tracking-tight">Glissez votre astrophoto ici</div>
                <div className="text-xs text-muted-foreground mt-1">ou cliquez pour sélectionner un fichier</div>
              </div>
            )}
            {file && (
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); setPreviewUrl(null); }}
                className="absolute top-3 right-3 p-1.5 rounded-md bg-black/60 border border-white/10 hover:bg-black/80"
                data-testid="clear-file-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
          className="md:col-span-4 rounded-md border border-white/10 bg-card flex flex-col"
        >
          <div className="px-5 py-3 border-b border-white/5">
            <div className="label-eyebrow">Étape 2 · Mode de traitement</div>
          </div>
          <div className="p-3 space-y-2 flex-1 overflow-auto" data-testid="mode-list">
            {modes.map((m) => {
              const active = mode === m.key;
              return (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  data-testid={`mode-${m.key}`}
                  className={`w-full text-left rounded-md border p-3 transition-all ${active ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/20 hover:bg-white/[0.02]"}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium tracking-tight text-sm">{m.label}</div>
                    {active && <div className="w-1.5 h-1.5 rounded-full bg-primary glow-violet" />}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">{m.summary}</div>
                </button>
              );
            })}
          </div>
          <div className="p-4 border-t border-white/5">
            <div className="label-eyebrow mb-3">Étape 3</div>
            <button
              onClick={onSubmit}
              disabled={busy || !file}
              data-testid="process-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-3 text-sm font-medium hover:bg-primary/90 transition-all glow-violet disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy ? <><div className="orbital !w-4 !h-4" />Sublimation en cours…</> : <><Sparkles className="w-4 h-4" />Sublimer l'image</>}
            </button>
            {selectedMode && (
              <ul className="mt-4 space-y-1">
                {selectedMode.details.map((d) => (
                  <li key={d} className="text-[11px] text-muted-foreground flex items-start gap-2">
                    <span className="text-primary">·</span>{d}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}


function ResultView({ result, onReset }) {
  const r = result.report;
  const id = result.id;
  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">{r.mode_label}</div>
          <h2 className="mt-2 text-3xl font-light tracking-tight">{result.name}</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {result.tags?.map((t) => (
              <span key={t} className="text-[11px] font-mono px-2 py-0.5 rounded border border-white/10 text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={exportUrl(id, "jpg")} className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2" data-testid="export-jpg"><Download className="w-3.5 h-3.5" />JPG</a>
          <a href={exportUrl(id, "png")} className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2" data-testid="export-png"><Download className="w-3.5 h-3.5" />PNG</a>
          <a href={exportUrl(id, "tif")} className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5 inline-flex items-center gap-2" data-testid="export-tif"><Download className="w-3.5 h-3.5" />TIFF</a>
          <button onClick={onReset} className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 glow-violet" data-testid="new-image-btn">Nouvelle image</button>
        </div>
      </div>
      <BeforeAfter beforeSrc={imageUrl(id, "original")} afterSrc={imageUrl(id, "processed")} />
      <QualityReport report={r} />
      <div className="rounded-md border border-white/10 bg-card p-5">
        <div className="label-eyebrow mb-3">Pipeline appliqué</div>
        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {r.steps?.map((s, i) => (
            <li key={i} className="text-sm flex items-start gap-3">
              <span className="font-mono text-[11px] text-violet-300 w-6">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
